// src/components/PrivacyPolicyPage.js
import React, { useState } from 'react';
import './PrivacyPolicyPage.css'; // Make sure this CSS file is ready for the new structure

// Import your fun images here!
import happyRoommatesImg from '../images/happy_roommates.jpg';
import supportTeamImg from '../images/support_team.jpg';

const PrivacyPolicyPage = () => {
    // State to manage the expansion of each accordion section
    const [expandedSections, setExpandedSections] = useState({});

    // Define the order of your policy sections for the accordion
    const policySectionOrder = [
        'scope', // Moved Scope into accordion
        'consent',
        'collection',
        'usage',
        'sensitive-data',
        'storage',
        'third-party',
        'data-breach',
        'grievance',
        'automated-decision',
        'security',
        'child-safety',
        // 'updates-contact' is now purely static
    ];

    // Handler for accordion header clicks
    const handleToggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId] // Toggle the boolean value for the clicked section
        }));
    };

    // Helper to get section title based on section ID
    const getSectionTitle = (sectionId) => {
        switch (sectionId) {
            case 'scope': return 'Scope and Applicability'; // New title for accordion
            case 'consent': return 'Consent and User Control'; // Updated title
            case 'collection': return 'Information We Collect';
            case 'usage': return 'How We Use Your Data';
            case 'sensitive-data': return 'Sensitive Data & Why We Collect Gender/DOB';
            case 'storage': return 'Data Storage and Retention';
            case 'third-party': return 'Third-Party Services';
            case 'data-breach': return 'Data Breach Policy';
            case 'grievance': return 'Grievance Mechanism';
            case 'automated-decision': return 'Automated Decision-Making';
            case 'security': return 'Security Measures';
            case 'child-safety': return 'Child Safety Standards';
            default: return '';
        }
    };

    // Object to store all policy content
    const policySectionsContent = {
        'scope': ( // Content for the new accordion item
            <div className="policy-content-block">
                <p>This Privacy Policy applies to:</p>
                <ul>
                    <li>Users who access or use the RoomEase mobile application or related services.</li>
                    <li>Any data collected, processed, and retained during registration, use, or interaction with our app.</li>
                </ul>
                <p className="highlight-text">
                    By using RoomEase, you consent to the collection, storage, and use of your personal data as described herein.
                </p>
            </div>
        ),
        'consent': (
            <div className="policy-content-block">
                <p>We obtain your explicit consent for:</p>
                <ul>
                    <li>Collecting mandatory data, such as name, phone number, email address, gender, and hobbies.</li>
                    <li>Using cookies and <b>tracking technologies</b> to enhance your app experience.</li>
                    <li>Fetching location data for match suggestions.</li>
                    {/* * NEW ADDITION: ATT Disclosure * */}
                    <li>
                        <strong>App Tracking Transparency (iOS):</strong> For users on Apple devices (iOS 14.5+), we use the App Tracking Transparency (ATT) framework to request your explicit permission to use identifiers (like IDFA) for <b>tracking</b> and targeted advertising. You have the right to decline this tracking. If declined, the data shared for advertising will be limited, and the ads you see may be less relevant.
                    </li>
                    {/* * */}
                </ul>
                <p>You may withdraw your consent at any time through the app settings, though this may limit app functionality.</p>
            </div>
        ),
        'collection': (
            <div className="policy-content-block">
                <h3>a. Mandatory Data</h3>
                <ul>
                    <li><strong>Personal Data:</strong> Name, phone number, email address, gender, and hobbies.</li>
                    <li><strong>Location Data:</strong> Approximate location fetched using Google API for match suggestions.</li>
                    <li><strong>Profile and Interaction Data:</strong> Roommate preferences, hobbies, chat history, and app usage activity.</li>
                    <li><strong>Device Data:</strong> Device type, operating system, and app usage patterns.</li>
                    {/* * NEW ADDITION: Tracking Identifiers * */}
                    <li>
                        <strong>Identifiers for Tracking:</strong> We collect your <b>Device ID</b> (such as IDFA) and <b>User ID</b> for advertising measurement and optimization.
                    </li>
                    {/*  */}
                </ul>
                <h3>b. Cookies and Tracking Technologies</h3>
                <div>
                    <ul>
                        <li><strong>Essential Cookies:</strong> Necessary for app functionality (e.g., maintaining login sessions).</li>
                        <li><strong>Preference Cookies:</strong> To remember your preferences (e.g., language, location).</li>
                        <li><strong>Analytics Cookies:</strong> To understand app usage and improve features using anonymized data.</li>
                        {/* * NEW ADDITION: Third-Party Tracking Disclosure * */}
                        <li>
                            <strong>Third-Party Tracking:</strong> We use SDKs (Software Development Kits) from third-party advertising partners that employ tracking technologies. This tracking is performed to link data collected from your app with data from other apps and services for targeted advertising and measurement purposes.
                        </li>
                        {/*  */}
                    </ul>
                </div>
                <h3>c. Instagram Username (Data Collection)</h3>
                <p>
                    We may request users to provide their Instagram username during profile creation or verification. This information is collected to gather additional user data and is not linked to Instagram or any third-party service.
                </p>
            </div>
        ),
        'usage': (
            <div className="policy-content-block">
                <p>We use your data for the following purposes:</p>
                <ul>
                    <li><strong>Roommate Matching:</strong> Suggesting compatible profiles based on gender, hobbies, and preferences.</li>
                    <li><strong>Personalization:</strong> Enhancing app features to provide a tailored user experience.</li>
                    <li><strong>Analytics and Service Improvement:</strong> Analyzing anonymized data to improve app functionality and resolve technical issues.</li>
                    <li><strong>Communication:</strong> Sending notifications, updates, and responding to inquiries.</li>
                    <li><strong>Compliance:</strong> Adhering to applicable legal and regulatory requirements.</li>
                    {/* * NEW ADDITION: Tracking Usage * */}
                    <li>
                        <strong>Advertising, Measurement, and Tracking:</strong> We use data, including Device ID, Product Interaction, and Purchase History (if applicable), to deliver, target, and measure personalized advertising both on and off the RoomEase app. This is done by linking your data with third-party data as defined by Apple’s tracking policies.
                    </li>
                    {/* * */}
                </ul>
                <h3>Purpose of Collection & Limitation for Instagram Username</h3>
                <p>
                    Instagram usernames provided by users are collected as additional user information. While we may use this data for manual verification, RoomEase does not guarantee the authenticity of the Instagram accounts provided. Users are solely responsible for providing accurate details.
                </p>
            </div>
        ),
        'sensitive-data': (
            <div className="policy-content-block">
                <p>
                    We collect sensitive data such as your phone number, email address, and gender to enable account creation, communication, and effective roommate matching. Sensitive data is stored securely and is not visible to other users.
                </p>
                <p>We handle this information in strict compliance with applicable laws and regulations.</p>
                <p>This data is strictly used for:</p>
                <ul>
                    <li>Account registration and authentication.</li>
                    <li>Secure communication and accurate roommate matching.</li>
                </ul>
                <p>Sensitive data is handled with the utmost priority and is never shared with unauthorized parties.</p>

                <h4>Why We Collect Gender and Date of Birth</h4>
                <p>Gender and Date of Birth (DOB) are mandatory for our app’s core functionality. These fields help us provide the best possible roommate matching experience.</p>
                <ul>
                    <li><strong>Gender:</strong> Helps match users based on compatibility, ensuring users can find roommates who align with their gender preferences.</li>
                    <li><strong>Date of Birth:</strong> Ensures that users are matched with others in the same or compatible age group, which is crucial for fostering a comfortable and suitable living environment.</li>
                </ul>
                <p>These fields are integral to the roommate matching process, ensuring relevant and suitable matches based on both gender preferences and age group.</p>
            </div>
        ),
        'storage': (
            <div className="policy-content-block">
                <p>All user data is stored on secure servers located in India and kept within India.</p>
                <p>Data is retained for 5 years, unless:</p>
                <ul>
                    <li>A special request for deletion is made</li>
                    <li>Retention is required for legal or regulatory reasons.</li>
                </ul>
                <p>Instagram usernames collected for verification and data enrichment purposes will be stored securely. Users may request the removal of this information, but this may impact their verification status on RoomEase.</p>
            </div>
        ),
        'third-party': (
            <div className="policy-content-block">
                <p>
                    We use trusted third-party services to provide core app functionality and to analyze and measure our marketing performance, including:
                </p>
                <ul>
                    <li><strong>Firebase:</strong> For app analytics and authentication services.</li>
                    <li><strong>Google API:</strong> For fetching approximate location data to suggest nearby matches.</li>
                    {/* * NEW ADDITION: Facebook/Meta * */}
                    <li>
                        <strong>Meta Platforms (Facebook):</strong> We use the Facebook SDK to measure the effectiveness of our advertising campaigns (attribution), and to analyze in-app events. Data shared with Meta may be used for <b>tracking</b> purposes (as defined above) to personalize the ads you see on and off Facebook's platform.
                    </li>
                    {/* * */}
                    <li><strong>WhatsApp:</strong> For enabling in-app redirection to WhatsApp chats when users opt to connect via the “Need a Room” feature.</li>
                </ul>
                <h3>General Disclaimer for Third-Party Services</h3>
                <p>
                    All such services operate under their own Terms of Service and Privacy Policies. RoomEase does not control and is not responsible for how these third parties handle your data. By using features that rely on these services, you agree to their respective policies.
                </p>
                <h3>WhatsApp-Specific Clause</h3>
                <ul>
                    <li>When you choose to use the WhatsApp integration, you will be redirected to the WhatsApp application or web platform.</li>
                    <li>All communications conducted via WhatsApp are governed solely by WhatsApp’s Terms of Service and Privacy Policy, and not by RoomEase.</li>
                    <li>RoomEase does not access, monitor, or store the content of any WhatsApp communications.</li>
                    <li>By using WhatsApp, you acknowledge that you voluntarily share your phone number and any personal data exchanged therein with the other party.</li>
                    <li>RoomEase disclaims all liability for any misuse, unauthorized disclosure, data leaks, or harm arising from communications conducted on WhatsApp.</li>
                </ul>
            </div>
        ),
        'data-breach': (
            <div className="policy-content-block">
                <p>In the event of a data breach:</p>
                <ul>
                    <li>Affected users will be notified promptly via email or app notifications.</li>
                    <li>Measures will be taken to secure user data and mitigate risks.</li>
                </ul>
            </div>
        ),
        'grievance': (
            <div className="policy-content-block">
                <p>We are committed to addressing grievances promptly:</p>
                <ul>
                    <li><strong>Acknowledgment:</strong> Your complaint will be noted within 3 working days.</li>
                    <li><strong>Resolution:</strong> Issues will be resolved within 30 working days.</li>
                </ul>
                <p>For assistance, contact:</p>
                <div className="contact-info">
                    <p><strong>Grievance Officer:</strong> Kushagra Arora</p>
                    <p><strong>Email:</strong> <a href="mailto:support@roomease.co.in">support@roomease.co.in</a></p>
                    <p><strong>Phone:</strong> <a href="tel:+919569994536">9569994536</a></p>
                </div>
            </div>
        ),
        'automated-decision': (
            <div className="policy-content-block">
                <p>Our algorithms are solely used to suggest compatible roommate matches based on shared preferences and data. No other significant decisions are automated.</p>
            </div>
        ),
        'security': (
            <div className="policy-content-block">
                <p>We implement robust measures to protect your data, including:</p>
                <ul>
                    <li><strong>Regular Audits:</strong> Conducting periodic security assessments to identify vulnerabilities.</li>
                    <li><strong>Backup Systems:</strong> Performing routine backups to prevent data loss.</li>
                </ul>
            </div>
        ),
        'child-safety': (
            <div className="policy-content-block">
                <h3>Age Requirement</h3>
                <p>RoomEase is strictly for users aged 18 and above. We do not knowingly collect data from children.</p>
                
                <h3>Zero Tolerance for CSAE</h3>
                <p>RoomEase explicitly prohibits the promotion, facilitation, or engagement in Child Sexual Abuse and Exploitation (CSAE). We do not tolerate any content, activities, or behavior that involves, harms, or exploits minors. Any account found to be involved in such activities will be permanently banned and reported to the relevant authorities immediately.</p>
                
                <h3>Reporting Mechanisms</h3>
                <p>User safety is our priority. We provide an in-app reporting mechanism to facilitate the reporting of any inappropriate behavior, including content that may involve child safety concerns. Users can report profiles, messages, or content directly within the RoomEase app (e.g., via the "Report" option in the chat or profile interface). All reports are reviewed by our safety team.</p>
                
                <h3>Contact Information</h3>
                <p>For any safety-related inquiries, concerns, or to report CSAM, please contact our dedicated Point of Contact:</p>
                <div className="contact-info">
                    <p><strong>Grievance Officer:</strong> Kushagra Arora</p>
                    <p><strong>Email:</strong> <a href="mailto:support@roomease.co.in">support@roomease.co.in</a></p>
                    <p><strong>Phone:</strong> <a href="tel:+919569994536">9569994536</a></p>
                </div>
            </div>
        ),
    };

    // Get the current date in a standard format (e.g., October 19, 2025)
    const lastUpdatedDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });


    return (
        <div className="privacy-policy-page">
            <header className="privacy-policy-header">
                <h1>Privacy Policy</h1>
                <p className="header-tagline">
                    {/* * UPDATED: Including Last Updated Date * */}
                    <strong>Effective Date: January 15, 2025</strong> | <strong>Last Updated: {lastUpdatedDate}</strong>
                    {/*  */}
                </p>
            </header>

            {/* New Static Welcome Section */}
            <section className="static-policy-section">
                <div className="container">
                    <div className="static-content-inner">
                        <div className="policy-content-block">
                            <h2>Welcome to RoomEase's Privacy Playground!</h2>
                            <p>
                                
                                <strong>RoomEase (“we,” “our,” or “us”)</strong> is committed to protecting your privacy. This Privacy Policy outlines the scope of our data practices, how we collect, use, store, and safeguard your personal data, and your rights under India’s Digital Personal Data Protection Act (DPDPA) and other applicable laws.
                            
                            </p>
                        </div>
                        <div className="policy-image-block">
                            <img src={happyRoommatesImg} alt="Happy Roommates" className="policy-image"/>
                            <p className="image-caption">Finding your perfect match, safely!</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Accordion Sections */}
            <section className="accordion-section">
                <div className="container">
                    {policySectionOrder.map((sectionId) => (
                        <div key={sectionId} className="accordion-item">
                            <button
                                className={`accordion-header ${expandedSections[sectionId] ? 'active' : ''}`}
                                onClick={() => handleToggleSection(sectionId)}
                                aria-expanded={expandedSections[sectionId]}
                                aria-controls={`accordion-content-${sectionId}`}
                            >
                                <h3>{getSectionTitle(sectionId)}</h3>
                                <span className="accordion-toggle-icon">
                                    {expandedSections[sectionId] ? '−' : '+'} {/* Plus/Minus icon */}
                                </span>
                            </button>
                            <div
                                id={`accordion-content-${sectionId}`}
                                className={`accordion-content ${expandedSections[sectionId] ? 'expanded' : 'collapsed'}`}
                                style={{ maxHeight: expandedSections[sectionId] ? '1500px' : '0' }}
                            >
                                {policySectionsContent[sectionId]}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Static Updates & Contact Section */}
            <section className="static-policy-section">
                <div className="container">
                    <div className="static-content-inner reverse-layout">
                        <div className="policy-image-block">
                            <img src={supportTeamImg} alt="Contact Us" className="policy-image"/>
                            <p className="image-caption">We're always here to listen!</p>
                        </div>
                        <div className="policy-content-block">
                            <h2>Keeping You in the Loop & How to Chat With Us!</h2>
                            <h3>Updates to This Privacy Policy</h3>
                            <p>
                                Significant updates to this policy will be communicated to users via email or in-app notifications.
                            </p>
                            <h3>Contact Us</h3>
                            <p>Got questions, thoughts, or just want to say hi? We're here!</p>
                            <div className="contact-info">
                                <p><strong>Email:</strong> <a href="mailto:support@roomease.co.in">support@roomease.co.in</a></p>
                                <p><strong>Phone:</strong> <a href="tel:+919569994536">9569994536</a></p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PrivacyPolicyPage;